import { QESButton, QESCheckbox, QESSelect, QESTextArea, QESTextInput } from "@app/shared/qes-standards";
import * as React from "react";
import { Flip, ToastPosition, toast, ToastOptions } from "react-toastify";
import { TooltipWrapper } from "@app/shared";
import { useClientId, useSSO } from "../hooks";
import { endpointBindingTypeOptionsSource, SSO, SSOStatus, ssoTypeOptionsSource } from "../models";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { JSX } from "react";

interface AccountSSOProps {
  clientId: number;
  disabled?: boolean;
}

const AccountSSO = React.memo((props: AccountSSOProps) => {
  const { clientId } = useClientId();
  const { beginMetadataDownload, endMetadataDownload, loadSSO, hasSSO, hasStatus, sso, status, toggleIsEnabled, updateSSO, validateCertificate } = useSSO();
  const clientContext = React.useContext(ClientContext);

  const [encryptedAssertionsCertValid, setEncryptedAssertionsCertValid] = React.useState(false);
  const [validatingEncryptedAssertionsCert, setValidatingEncryptedAssertionsCert] = React.useState(false);

  const validateEncryptedAssertionsCert = async (certBase64: string) => {
    if (!certBase64 || certBase64.length == 0)
      return true; // skip validation of empty strings

    setValidatingEncryptedAssertionsCert(true);
    const isValid = await validateCertificate(certBase64);
    setEncryptedAssertionsCertValid(isValid);
    setValidatingEncryptedAssertionsCert(false);
    return isValid;
  }

  const [authRequestCertValid, setAuthRequestCertValid] = React.useState(false);
  const [validatingAuthRequestCert, setValidatingAuthRequestCert] = React.useState(false);

  const validateAuthRequestCert = async (certBase64: string) => {
    if (!certBase64 || certBase64.length == 0)
      return true; // skip validation of empty strings

    setValidatingAuthRequestCert(true);
    const isValid = await validateCertificate(certBase64);
    setAuthRequestCertValid(isValid);
    setValidatingAuthRequestCert(false);
    return isValid;
  }

  React.useEffect(() => {
    if (clientId && hasStatus([SSOStatus.Not_Loaded])) {
      loadSSO();
    }
  }, [clientId]);
  React.useEffect(() => {
    let downloadTimer: NodeJS.Timeout;
    if (hasStatus([SSOStatus.Saving, SSOStatus.Saved, SSOStatus.Save_Failed])) {
      makeToast(status);
    }

    if (hasStatus([SSOStatus.Download_Metadata])) {
      downloadTimer = setTimeout(endMetadataDownload, 500);
    }

    return () => clearTimeout(downloadTimer);
  }, [status]);

  const metadataRef = React.createRef<HTMLInputElement>();
  const defaultProps = { disabled: props.disabled, onPropertyChange: updateSSO, readOnly: hasStatus([SSOStatus.Saving]), source: sso };
  const canCopy = navigator.clipboard; // returns undefined if not supported by browser

  return (
    <>
      <div>
        <div hidden={!hasSSO}>
          <div className="col-12">
            <QESCheckbox
              label={!!sso.isEnabled ? "Enabled" : "Disabled"}
              id="sso-isEnabled"
              checked={sso.isEnabled}
              onChange={(e) => { toggleIsEnabled(); clientContext.setSsoIsEnabled(e.target.checked); }}
              aria-label="Toggle Single Sign-On Enabled"
              disabled={defaultProps.readOnly}
              testId="account__sso-is-enabled"
            />
          </div>
        </div>

        <div className="col-12 text-muted">
          <span className="demarcation" data-text-position="left"><strong>SSO Options</strong></span>
        </div>

        <SSOInputText label="Name" containerClass="col-8" propertyKey="displayName" setValidation={clientContext.setSsoDisplayName} isValid={clientContext.validationObject["ssoDisplayName"]} required={clientContext.requiredFields["ssoDisplayName"]} {...defaultProps} />

        <SSOSelect label="SSO Type" propertyKey="ssoType" optionsSource={ssoTypeOptionsSource} readOnly={(hasSSO)} containerClass="col-3" setValidation={clientContext.setSsoType} {...defaultProps} />

        <SSOInputText label="Name Identifier Claim Type" propertyKey="nameIdentifierClaimType" setValidation={clientContext.setSsoNameIdentifierClaimType} isValid={clientContext.validationObject["ssoNameIdentifierClaimType"]} required={clientContext.requiredFields["ssoNameIdentifierClaimType"]} {...defaultProps} />

        <SSOInputText label="Email Claim Type" propertyKey="emailClaimType" setValidation={clientContext.setSsoEmailClaimType} isValid={clientContext.validationObject["ssoEmailClaimType"]} required={clientContext.requiredFields["ssoEmailClaimType"]} {...defaultProps} />

        <div className="mt20">
          <div className="col-12 text-muted">
            <span className="demarcation" data-text-position="left"><strong>Identity Provider Options</strong></span>
          </div>
        </div>

        <SSOInputText label="Entity Id" containerClass="col-8" propertyKey="idpEntityId" setValidation={clientContext.setSsoIdpEntityId} isValid={clientContext.validationObject["ssoIdpEntityId"]} required={clientContext.requiredFields["ssoIdpEntityId"]} {...defaultProps} />
        <div className="row">
          <div className="col-7">
            <SSOInputText label="SSO Endpoint" propertyKey="idpSingleSignOnEndpoint" setValidation={clientContext.setSsoIdpSingleSignOnEndpoint} isValid={clientContext.validationObject["ssoIdpSingleSignOnEndpoint"]} required={clientContext.requiredFields["ssoIdpSingleSignOnEndpoint"]} {...defaultProps} />
          </div>
          <div className="col-5">
            <SSOSelect label="Endpoint Binding Type" propertyKey="idpSignOnEndpointBindingType" optionsSource={endpointBindingTypeOptionsSource} containerClass="col-5" setValidation={clientContext.setSsoIdpSignOnEndpointBindingType} {...defaultProps} disabled={!sso.idpSingleSignOnEndpoint || sso.idpSingleSignOnEndpoint.length === 0} />
          </div>
        </div>

        <SigningCertMultiSelect />

        <div className="mt20">
          <div className="col-12 text-muted">
            <span className="demarcation" data-text-position="left"><strong>Service Provider Options</strong></span>
          </div>
        </div>

        <div hidden={!hasSSO}>
          <label className="qes-text-input__label col-12" htmlFor="account-sso__metadata-path">Metadata Path</label>
          <div className="qes-text-input__container col-10 col-md-8 col-lg-6 col-xl-4">
            <input className="qes-text-input__input" data-testid="account-sso__metadata-path" id="account-sso__metadata-path" ref={metadataRef} readOnly={true} value={`${window.authEndpoint}${sso.spMetadataPath}`} />
          </div>
          <div className="col-2 pl-0">
            <TooltipWrapper text="Download metadata" triggerWidth="100%">
              <QESButton qesType="secondary" className="px-2" aria-label="Download" qesTestId="account-sso__downloadmetadata" onClick={beginMetadataDownload} disabled={defaultProps.disabled}>
                <i className="fa fa-download" />
              </QESButton>
            </TooltipWrapper>
            {canCopy &&
              (
              <TooltipWrapper containerClassName="pl10" text="Copy to clipboard" triggerWidth="100%">
                <QESButton qesType="secondary" className="px-2" aria-label="Copy path to clipboard" qesTestId="account-sso__copymetadatapath" onClick={() => { metadataRef.current.select(); navigator.clipboard.writeText(`${metadataRef.current.value}`); }} disabled={defaultProps.disabled}>
                    <i className="fa fa-copy" />
                  </QESButton>
                </TooltipWrapper>
              )
            }
          </div>
        </div>

        <div hidden={!hasStatus([SSOStatus.Download_Metadata])}>
          <div className="col-12">
            <iframe id="my_iframe" className="d-none" src={!hasStatus([SSOStatus.Download_Metadata]) ? "" : `${window.authEndpoint}${sso.spMetadataPath}`} />
          </div>
        </div>

        <div className="mb-10">
          <div className="col-12">
            <QESCheckbox
              label="Require Encrypted Assertions"
              id="sso-requireencryptedassertions"
              checked={!!sso.spRequireEncryptedAssertions}
              onChange={(e) => { updateSSO("spRequireEncryptedAssertions", !sso.spRequireEncryptedAssertions); clientContext.setSsoSpRequireEncryptedAssertions(e.target.checked); }}
              aria-label="Toggle Require Encrypted Assertions"
              disabled={defaultProps.disabled || defaultProps.readOnly}
              testId="account__sso-require-encrypted-assertions"
            />
          </div>
        </div>

        <SSOInputText
          label="Encrypted Assertions Certificate"
          propertyKey="spEncryptionCertificateBase64"
          {...defaultProps}
          disabled={!sso.spRequireEncryptedAssertions || defaultProps.disabled}
          onPropertyChange={(key, value) => validateEncryptedAssertionsCert(value as string).then(() => updateSSO(key, value))}
          setValidation={clientContext.setSsoSpEncryptionCertificateBase64}
          required={clientContext.requiredFields["ssoSpEncryptionCertificateBase64"]}
          isValid={(validatingEncryptedAssertionsCert || encryptedAssertionsCertValid) && clientContext.validationObject["ssoSpEncryptionCertificateBase64"]}
          errorText="Certificate is not a valid Base64-encoded X509 certificate."
        />

        <div className="col-12">
          <QESCheckbox
            label="Sign Authentication Requests"
            id="sso-signauthenticationrequests"
            checked={!!sso.spSignAuthenticationRequests}
            onChange={(e) => { updateSSO("spSignAuthenticationRequests", !sso.spSignAuthenticationRequests); clientContext.setSsoSpSignAuthenticationRequests(e.target.checked); }}
            aria-label="Toggle Sign Authentication Requests"
            disabled={defaultProps.disabled || defaultProps.readOnly}
            testId="account__sso-sign-authentication-requests"
          />
        </div>

        <SSOInputText
          label="Authentication Request Signing Certificate"
          propertyKey="spSigningCertificateBase64"
          {...defaultProps}
          disabled={!sso.spSignAuthenticationRequests || defaultProps.disabled}
          onPropertyChange={(key, value) => validateAuthRequestCert(value as string).then(() => updateSSO(key, value))}
          setValidation={clientContext.setSsoSpSigningCertificateBase64}
          required={clientContext.requiredFields["ssoSpSigningCertificateBase64"]}
          isValid={(validatingAuthRequestCert || authRequestCertValid) && clientContext.validationObject["ssoSpSigningCertificateBase64"]}
          errorText="Certificate is not a valid Base64-encoded X509 certificate."
        />
      </div>
    </>
  );
});

AccountSSO.displayName = "AccountSSO";

export { AccountSSO };

const SigningCertMultiSelect = React.memo(() => {
  const [newCertValue, setNewCertValue] = React.useState<string>("");
  const [certificateRemoved, setCertificateRemoved] = React.useState(false);
  const [certificateIsValid, setCertificateIsValid] = React.useState(false);
  const [validatingCert, setValidatingCert] = React.useState(false);
  const { sso, hasStatus, updateSSO, validateCertificate } = useSSO();
  const clientContext = React.useContext(ClientContext);

  const addCert = () => {
    const certs = [...(sso.idpSigningCertificatesBase64 || [])];
    certs.push(newCertValue);
    updateSSO("idpSigningCertificatesBase64", certs);
    setNewCertValue("");

    clientContext.setSsoIdpSigningCertificateBase64(certs);
  };

  const handleNewCertValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    validateCert(value).then(() => setNewCertValue(value));
  };

  const handleNewCertInputKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !!newCertValue && !validatingCert && certificateIsValid) {
      addCert();
    }
  };

  const validateCert = async (certBase64: string) => {
    if (!certBase64 || certBase64.length == 0)
      return true; // skip validation of empty strings

    setValidatingCert(true);
    const isValid = await validateCertificate(certBase64);
    setCertificateIsValid(isValid);
    setValidatingCert(false);
    return isValid;
  }

  const removeCert = (index: number) => {
    const certs = [...sso.idpSigningCertificatesBase64];
    certs.splice(index, 1);
    updateSSO("idpSigningCertificatesBase64", certs);

    clientContext.setSsoIdpSigningCertificateBase64(certs);
    setCertificateRemoved(true);
  };

  return (
    <>
      <div className=" col-12">
        <div className="row">
          <QESTextArea
            containerClassName="col-11"
            label="Signing Certificates"
            testId={`account-sso__new-signing-cert`}
            value={newCertValue}
            required={clientContext.requiredFields["ssoIdpSigningCertificateBase64"]}
            onChange={handleNewCertValueChange}
            readOnly={hasStatus([SSOStatus.Saving])}
            onKeyUp={handleNewCertInputKeyUp}
            wasFocusedExternally={certificateRemoved}
            isValid={validatingCert || certificateIsValid}
            errorText="Certificate is not a valid Base64-encoded X509 certificate."
          />
          <div className="col-1 pl-0 pr-0">
            <TooltipWrapper text="Add certificate" triggerWidth="100%">
              <QESButton qesType="secondary" className="px-1 mt-3" disabled={validatingCert || !certificateIsValid || !newCertValue || hasStatus([SSOStatus.Saving])} qesTestId="account-sso__add-signing-cert" onClick={addCert} aria-label="Add Certificate">
                <i className="fa fa-plus" />
              </QESButton>
            </TooltipWrapper>
          </div>
        </div>
      </div>

      <div className=" col-12">
        {
          !sso.idpSigningCertificatesBase64 ? null : sso.idpSigningCertificatesBase64.map((cert, i) => {
            return (
              <div className="row mb-2" key={i}>
                <QESTextArea containerClassName="col-10 offset-1" label="" testId={`account-sso__signing-cert-${i}`} value={`${cert}`} readOnly={true} />
                <div className="col-1 pl-0 pr-0">
                  <TooltipWrapper text="Remove certificate" triggerWidth="100%">
                    <QESButton qesType="secondary-danger" className="px-1" qesTestId={`account-sso__remove-signing-cert-${i}`} onClick={() => removeCert(i)} aria-label="Delete Certificate">
                      <i className="fa fa-trash" />
                    </QESButton>
                  </TooltipWrapper>
                </div>
              </div>
            );
          })
        }
      </div>
    </>
  );
});

SigningCertMultiSelect.displayName = "SigningCertMultiSelect";

type OnPropertyChange = (propertyKey: keyof SSO, propertyValue: boolean | string) => void;
type SSOInputTextProps = {
  containerClass?: string;
  errorText?: string;
  label: string;
  onPropertyChange: OnPropertyChange;
  propertyKey: keyof SSO;
  setValidation: React.Dispatch<React.SetStateAction<string>>;
  source: SSO;
  isValid: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const SSOInputText: React.FC<React.PropsWithChildren<SSOInputTextProps>> = ({ containerClass, onPropertyChange, propertyKey, setValidation, source, ...rest }) => {
  const props = {
    containerClassName: containerClass ? `${containerClass} mb-3` : "col-12 mb-3",
    value: source[propertyKey] !== undefined ? source[propertyKey].toString() : "",
    ...rest,
  };
  return (
    <QESTextInput
      onChange={(e) => { onPropertyChange(propertyKey, e.target.value); setValidation(e.target.value); }}
      testId={`account-sso__${propertyKey.toLowerCase()}`}
      {...props}
    />
  );
};

type SSOSelectProps = {
  containerClass?: string;
  label: string;
  onPropertyChange: OnPropertyChange;
  optionsSource: Record<string, string>;
  propertyKey: keyof SSO;
  setValidation: React.Dispatch<React.SetStateAction<unknown>>;
  source: SSO;
} & React.InputHTMLAttributes<HTMLSelectElement>;

const SSOSelect: React.FC<React.PropsWithChildren<SSOSelectProps>> = ({ containerClass, onPropertyChange, optionsSource, propertyKey, readOnly, setValidation, source, ...rest }) => {
  const props = {
    containerClassName: containerClass ? `${containerClass} mb-3` : "col-12 mb-3",
    testId: `account-sso__${propertyKey.toLowerCase()}`,
    value: source[propertyKey] !== undefined ? source[propertyKey].toString() : "",
    ...rest,
  };

  if (readOnly) {
    const defaultValue = optionsSource[props.value.toString()];
    return (
      <QESTextInput
        containerClassName={props.containerClassName}
        label={props.label}
        readOnly={true}
        testId={props.testId}
        defaultValue={defaultValue ? defaultValue : optionsSource[props.value.toString().toUpperCase()]}
      />
    );
  }

  return (
    <QESSelect
      onChange={(e) => { onPropertyChange(propertyKey, e.target.value); setValidation(e.target.value); }}
      placeholder="Select One"
      {...props}
    >
      {Object.keys(optionsSource).map((key) => (
        <option key={key} value={key}>{optionsSource[key]}</option>
      ))}
    </QESSelect>
  );
};

//TODO: Make hook
function makeToast(toastType: SSOStatus): void {
  const toastId = "burnt-toast";
  let toastOptions: ToastOptions = {
    autoClose: 2500 as number | false,
    closeButton: true,
    closeOnClick: true,
    containerId: "z-index-1030",
    hideProgressBar: false,
    pauseOnFocusLoss: false,
    pauseOnHover: false,
    position: "top-center" as ToastPosition,
    toastId,
    transition: Flip,
    type: "info",
  };
  let content: JSX.Element;

  if (toastType === SSOStatus.Saved) {
    content = (<span><i className="fa fa-check" />&nbsp;&nbsp;Changes Saved!</span>);
  } else if (toastType === SSOStatus.Saving) {
    content = (<span><div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Saving Changes...</span></div>&nbsp;&nbsp;Saving Changes...</span>);
    toastOptions = { ...toastOptions, autoClose: false, closeButton: false, closeOnClick: false, hideProgressBar: true };
  } else if (toastType === SSOStatus.Save_Failed) {
    content = (<><span><i className="fa fa-ban" />&nbsp;&nbsp;Failed to Save Changes!</span></>);
    toastOptions = { ...toastOptions, autoClose: false, type: "error" };
  }

  if (toast.isActive(toastId)) {
    toast.update(toastId, { ...toastOptions, render: content });
  } else {
    toast.info(content, toastOptions);
  }
}